import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { EnvelopeSimple } from "@phosphor-icons/react";
import { Logo } from "../components/Logo";

function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
      <div className="w-full max-w-md card-crisp bg-white p-8">
        <div className="mb-8"><Logo size={44} /></div>
        <h1 className="font-heading text-3xl font-extrabold text-slate-900 mb-1">{title}</h1>
        <p className="text-sm text-slate-600 mb-8">{subtitle}</p>
        {children}
      </div>
    </div>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Esqueceu a senha?" subtitle="Informe seu e-mail e enviaremos um link para criar uma nova senha.">
      {sent ? (
        <div className="rounded-md bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-800" data-testid="forgot-success">
          <EnvelopeSimple size={20} className="mb-2" />
          Se o e-mail <strong>{email}</strong> estiver cadastrado, você receberá um link de redefinição em instantes. Verifique também a caixa de spam.
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="email">E-mail</Label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1" data-testid="forgot-email" />
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="forgot-submit">
            {loading ? "Enviando..." : "Enviar link de redefinição"}
          </Button>
        </form>
      )}
      <p className="mt-6 text-sm text-slate-600">
        <Link to="/login" className="text-indigo-600 font-medium hover:underline" data-testid="link-back-login">Voltar para o login</Link>
      </p>
    </AuthShell>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) return toast.error("As senhas não coincidem");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Senha redefinida! Faça login com a nova senha.");
      navigate("/login");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <AuthShell title="Link inválido" subtitle="Este link de redefinição não é válido.">
        <Link to="/forgot-password" className="text-indigo-600 font-medium hover:underline">Solicitar novo link</Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Criar nova senha" subtitle="Escolha uma nova senha com pelo menos 6 caracteres.">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label htmlFor="password">Nova senha</Label>
          <Input id="password" type="password" minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1" data-testid="reset-password" />
        </div>
        <div>
          <Label htmlFor="confirm">Confirmar senha</Label>
          <Input id="confirm" type="password" minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} required className="mt-1" data-testid="reset-confirm" />
        </div>
        <Button type="submit" disabled={loading} className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="reset-submit">
          {loading ? "Salvando..." : "Salvar nova senha"}
        </Button>
      </form>
      <p className="mt-6 text-sm text-slate-600">
        Link não funcionou? <Link to="/forgot-password" className="text-indigo-600 font-medium hover:underline" data-testid="link-new-reset">Solicitar um novo link</Link>
      </p>
    </AuthShell>
  );
}
